import { useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link, Navigate, useNavigate, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { AddressProofUpload } from "~/components/address-proof-upload";
import { useConfirm } from "~/components/confirm-dialog";
import { DeliveryPicker, deliveryError, type DeliveryValue } from "~/components/delivery-picker";
import { Field, fieldMessageId } from "~/components/field";
import { FlowLayout } from "~/components/flow-layout";
import { NotifyPrompt } from "~/components/notify-prompt";
import { RoutePicker } from "~/components/route-picker";
import { ShippingEstimate } from "~/components/shipping-estimate";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { api, APIError } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { COUNTRIES } from "~/lib/countries";
import { formatDay, formatEta } from "~/lib/format";
import { requireCustomer } from "~/lib/guards";
import { MODE_LABEL, type ShipMode } from "~/lib/modes";
import type { IParcel } from "~/lib/proto";
import { useRouteSelection } from "~/lib/use-route-selection";

type Step = 1 | 2 | 3;
type Search = { origin?: string; mode?: ShipMode; step?: Step; done?: string };

export const Route = createFileRoute("/_authed/register")({
  validateSearch: (s: Record<string, unknown>): Search => {
    const step = Number(s.step);
    return {
      origin: typeof s.origin === "string" ? s.origin.toUpperCase() : undefined,
      mode: s.mode === "sea" || s.mode === "air" || s.mode === "road" ? s.mode : undefined,
      step: step === 2 || step === 3 ? step : undefined,
      done: typeof s.done === "string" ? s.done : undefined,
    };
  },
  beforeLoad: async ({ context }) => {
    await requireCustomer(context.queryClient);
  },
  component: AddParcel,
});

type ParcelFields = { tracking: string; expectedArrival: string; description: string; declaredValue: string };
type ParcelErrors = { tracking?: string; expectedArrival?: string };
type Errors = ParcelErrors & { route?: string; delivery?: string | null; proof?: string; form?: string };

const STEP_COUNT = 3;
const EMPTY_PARCEL: ParcelFields = { tracking: "", expectedArrival: "", description: "", declaredValue: "" };

function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function latestArrival(): string {
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() + 1);
  return isoDay(limit);
}

function parcelErrors(parcel: ParcelFields): ParcelErrors {
  const errors: ParcelErrors = {};
  if (parcel.tracking.replace(/\s/g, "").length < 5) {
    errors.tracking = parcel.tracking.trim()
      ? "That looks too short. Copy the full tracking number from the seller."
      : "Enter the tracking number from the seller.";
  }
  if (!parcel.expectedArrival) errors.expectedArrival = "Choose the delivery date the seller gave you.";
  else if (parcel.expectedArrival > latestArrival()) errors.expectedArrival = "That date is more than a year away. Check it and try again.";
  return errors;
}

function countryName(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

function AddParcel() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data: me } = useMe();
  const flowStart = useRef(router.history.location.state.__TSR_index);

  const route = useRouteSelection({ origin: search.origin, mode: search.mode });
  const [parcel, setParcel] = useState<ParcelFields>(EMPTY_PARCEL);
  const [delivery, setDelivery] = useState<DeliveryValue>({ method: "", area: "", address: "" });
  const [proof, setProof] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<IParcel | null>(null);

  const routeReady = !!route.origin && (route.served || route.checking);
  const parcelReady = Object.keys(parcelErrors(parcel)).length === 0;
  const reachable: Step = !routeReady ? 1 : !parcelReady ? 2 : 3;
  const step = Math.min(search.step ?? 1, reachable) as Step;

  function historyIndex(): number {
    return router.history.location.state.__TSR_index;
  }

  function goTo(next: Step, replace = false) {
    void navigate({ search: (prev) => ({ ...prev, step: next === 1 ? undefined : next, done: undefined }), replace });
  }

  function returnTo(previous: Step) {
    if (historyIndex() > flowStart.current) router.history.back();
    else goTo(previous, true);
  }

  async function leave() {
    const started = !!(parcel.tracking || parcel.expectedArrival || parcel.description || proof);
    if (started) {
      const discard = await confirm({
        title: "Discard this parcel?",
        body: "What you have entered so far will not be saved.",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        destructive: true,
      });
      if (!discard) return;
    }
    await navigate({ to: "/" });
  }

  function back() {
    if (step === 1) void leave();
    else returnTo((step - 1) as Step);
  }

  function setField(field: keyof ParcelFields, value: string) {
    setParcel((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
  }

  function continueFromRoute() {
    if (!route.origin) return setErrors({ route: "Choose where your parcel is coming from." });
    if (!routeReady) return setErrors({ route: "We do not ship this route yet. Choose another one." });
    setErrors({});
    goTo(2);
  }

  function continueFromParcel() {
    const found = parcelErrors(parcel);
    setErrors(found);
    const firstInvalid = found.tracking ? "tracking" : found.expectedArrival ? "expected" : null;
    if (firstInvalid) return document.getElementById(firstInvalid)?.focus();
    goTo(3);
  }

  async function addParcel() {
    const found: Errors = {
      delivery: deliveryError(delivery, me),
      proof: proof ? undefined : "Add a photo or screenshot of the address.",
    };
    setErrors(found);
    if (found.delivery || found.proof) return;

    setBusy(true);
    try {
      const created = await api.registerParcel({
        originCountry: route.origin,
        mode: route.mode,
        trackingNumber: parcel.tracking.trim(),
        expectedArrival: parcel.expectedArrival,
        description: parcel.description.trim() || undefined,
        declaredValueKes: parcel.declaredValue ? Math.round(Number(parcel.declaredValue) * 100) : undefined,
        deliveryMethod: delivery.method,
        deliveryArea: delivery.method === "locker" ? delivery.area.trim() : undefined,
        deliveryAddress: delivery.method === "custom" ? delivery.address.trim() : undefined,
        addressProof: proof,
      });
      void qc.invalidateQueries({ queryKey: ["parcels"] });
      setAdded(created);
      setParcel(EMPTY_PARCEL);
      setProof("");
      flowStart.current = historyIndex();
      await navigate({ search: { origin: route.origin, mode: route.mode, done: created.id ?? undefined }, replace: true });
    } catch (e) {
      handleAddError(e);
    } finally {
      setBusy(false);
    }
  }

  function handleAddError(e: unknown) {
    if (!(e instanceof APIError)) return setErrors({ form: "No connection. Check your internet and try again." });
    if (e.code === "tracking_taken") {
      setErrors({ tracking: "This tracking number has already been added. Check your parcels." });
      return returnTo(2);
    }
    if (e.code === "invalid_expected_arrival") {
      setErrors({ expectedArrival: "The warehouse cannot use that date. Check it and try again." });
      return returnTo(2);
    }
    if (e.code === "address_proof_required") {
      setProof("");
      return setErrors({ proof: "We lost the photo. Add it again." });
    }
    if (e.code === "profile_incomplete") return setErrors({ delivery: "Save your home address to use home delivery." });
    setErrors({ form: "We could not add your parcel. Try again." });
  }

  if (search.done) {
    if (added?.id !== search.done) return <Navigate to="/parcel/$id" params={{ id: search.done }} replace />;
    return <Added parcel={added} onAddAnother={() => goTo(2, true)} />;
  }

  if (step === 1) {
    return (
      <FlowLayout
        step={1}
        stepCount={STEP_COUNT}
        title="Where is it coming from?"
        description="Choose the warehouse you had your order sent to."
        submitLabel="Continue"
        onBack={back}
        onSubmit={continueFromRoute}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <span id="coming-from" className="sr-only">
              Where is it coming from?
            </span>
            <RoutePicker route={route} labelledBy="coming-from" />
            {errors.route && !routeReady && (
              <p role="alert" className="text-destructive type-footnote">
                {errors.route}
              </p>
            )}
            {route.origin && !routeReady && !errors.route && (
              <p className="text-muted-foreground type-footnote">We do not ship this route yet. Choose another one.</p>
            )}
          </div>
          {route.origin && routeReady && <ShippingEstimate key={`${route.origin}-${route.mode}`} origin={route.origin} mode={route.mode} />}
          <p className="text-muted-foreground type-footnote">
            Not ordered yet?{" "}
            <Link to="/addresses" className="text-foreground font-medium underline underline-offset-4">
              Get your shipping address first
            </Link>
          </p>
        </div>
      </FlowLayout>
    );
  }

  if (step === 2) {
    return (
      <FlowLayout
        step={2}
        stepCount={STEP_COUNT}
        title="Parcel details"
        description="You will find these in the seller's shipping confirmation."
        submitLabel="Continue"
        onBack={back}
        onSubmit={continueFromParcel}
      >
        <div className="space-y-5">
          <Field
            id="tracking"
            label="Tracking number"
            error={errors.tracking}
            hint="We send it to the warehouse so they can find your box."
          >
            <Input
              id="tracking"
              density="comfortable"
              autoFocus
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="1Z999AA10123456784"
              value={parcel.tracking}
              aria-invalid={!!errors.tracking || undefined}
              aria-describedby={fieldMessageId("tracking")}
              onChange={(e) => setField("tracking", e.target.value)}
            />
          </Field>
          <Field
            id="expected"
            label="Seller's delivery date"
            error={errors.expectedArrival}
            hint="The day the seller says it reaches the warehouse. We start checking for it from then."
          >
            <Input
              id="expected"
              type="date"
              density="comfortable"
              max={latestArrival()}
              value={parcel.expectedArrival}
              aria-invalid={!!errors.expectedArrival || undefined}
              aria-describedby={fieldMessageId("expected")}
              onChange={(e) => setField("expectedArrival", e.target.value)}
            />
          </Field>
          <Field id="description" label="What is it?" optional>
            <Input
              id="description"
              density="comfortable"
              placeholder="Nike sneakers"
              value={parcel.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </Field>
          <Field id="value" label="Value in KES" optional>
            <Input
              id="value"
              density="comfortable"
              inputMode="numeric"
              placeholder="12000"
              value={parcel.declaredValue}
              onChange={(e) => setField("declaredValue", e.target.value.replace(/[^\d.]/g, ""))}
            />
          </Field>
        </div>
      </FlowLayout>
    );
  }

  return (
    <FlowLayout
      step={3}
      stepCount={STEP_COUNT}
      title="Delivery in Kenya"
      description="How should we get it to you once it lands? You can change this until it arrives in Kenya."
      submitLabel="Add parcel"
      submitting={busy}
      formError={errors.form}
      onBack={back}
      onSubmit={() => void addParcel()}
    >
      <div className="space-y-8">
        <DeliveryPicker
          value={delivery}
          error={errors.delivery}
          onChange={(next) => {
            setDelivery(next);
            setErrors((prev) => ({ ...prev, delivery: undefined, form: undefined }));
          }}
        />

        <section className="space-y-3">
          <div>
            <h2 className="type-headline">Photo of the address</h2>
            <p className="text-muted-foreground type-footnote mt-1">
              A screenshot of the delivery address on your order. The warehouse uses it to confirm the parcel is yours.
            </p>
          </div>
          <AddressProofUpload
            value={proof}
            error={errors.proof}
            onChange={(token) => {
              setProof(token);
              setErrors((prev) => ({ ...prev, proof: undefined, form: undefined }));
            }}
          />
        </section>

        <section className="space-y-3">
          <h2 className="type-headline">Check before you add</h2>
          <dl className="border-hairline bg-surface divide-hairline divide-y rounded-lg border">
            <SummaryRow label="From" onEdit={() => goTo(1)}>
              {countryName(route.origin)}, by {MODE_LABEL[route.mode].toLowerCase()}
            </SummaryRow>
            <SummaryRow label="Tracking number" onEdit={() => goTo(2)}>
              <span className="wrap-anywhere">{parcel.tracking.trim().toUpperCase()}</span>
            </SummaryRow>
            <SummaryRow label="Seller's delivery date" onEdit={() => goTo(2)}>
              {formatDay(`${parcel.expectedArrival}T00:00:00`)}
            </SummaryRow>
          </dl>
        </section>
      </div>
    </FlowLayout>
  );
}

function SummaryRow({ label, onEdit, children }: { label: string; onEdit: () => void; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2 pr-2 pl-4">
      <div className="min-w-0 flex-1">
        <dt className="text-muted-foreground text-[13px]">{label}</dt>
        <dd className="text-[15px] font-medium">{children}</dd>
      </div>
      <Button type="button" variant="ghost" className="h-11 shrink-0 px-3 text-[14px]" onClick={onEdit}>
        Edit
      </Button>
    </div>
  );
}

function Added({ parcel, onAddAnother }: { parcel: IParcel; onAddAnother: () => void }) {
  const eta = formatEta(parcel.etaMinAt, parcel.etaMaxAt);
  return (
    <div
      className="bg-background text-foreground flex min-h-svh flex-col px-5"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10 text-center">
        <div className="bg-success/15 mx-auto grid size-16 place-items-center rounded-full">
          <Check className="size-8" strokeWidth={2} />
        </div>
        <h1 className="type-title mt-6">Parcel added</h1>
        <p className="text-muted-foreground type-body mt-2 text-balance">
          We have told our {countryName(parcel.originCountry ?? "")} warehouse to expect it. We will text you when it gets
          there.
        </p>
        <dl className="border-hairline bg-surface divide-hairline mt-6 divide-y rounded-lg border text-left">
          <div className="px-4 py-3">
            <dt className="text-muted-foreground text-[13px]">Tracking number</dt>
            <dd className="text-[15px] font-medium wrap-anywhere">{parcel.trackingNumber}</dd>
          </div>
          {eta && (
            <div className="px-4 py-3">
              <dt className="text-muted-foreground text-[13px]">Estimated delivery in Kenya</dt>
              <dd className="text-[15px] font-medium">{eta}</dd>
            </div>
          )}
        </dl>
        <div className="mt-4">
          <NotifyPrompt
            title="Know the moment it arrives"
            body="We can notify you on this device at every step, as well as by SMS."
          />
        </div>
        <div className="mt-6 space-y-3">
          <Button asChild size="touch" className="w-full">
            <Link to="/parcel/$id" params={{ id: parcel.id! }}>
              View parcel
            </Link>
          </Button>
          <Button type="button" variant="outline" size="touch" className="w-full" onClick={onAddAnother}>
            Add another parcel
          </Button>
        </div>
      </div>
    </div>
  );
}
